import {
  getCsrfToken,
  getCurrentUser,
  signInWithPassword,
  signUpWithPassword,
  signOut as apiSignOut,
  requestPasswordReset,
  completePasswordReset,
  exchangeClerkSession,
  type AuthUser,
} from "@workspace/api-client-react";

const CSRF_COOKIE = "kd_csrf";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

let csrfPromise: Promise<string> | null = null;

export async function getCsrf(): Promise<string> {
  const existing = readCookie(CSRF_COOKIE);
  if (existing) return existing;
  if (!csrfPromise) {
    csrfPromise = getCsrfToken()
      .then((res) => res.token)
      .finally(() => {
        csrfPromise = null;
      });
  }
  return csrfPromise;
}

export function csrfInit(token: string): RequestInit {
  return { headers: { "x-csrf-token": token } };
}

export async function fetchMe(): Promise<AuthUser | null> {
  const res = await getCurrentUser();
  return res.user ?? null;
}

export async function signIn(email: string, password: string): Promise<AuthUser> {
  const token = await getCsrf();
  const res = await signInWithPassword({ email, password }, csrfInit(token));
  return res.user;
}

export async function signUp(
  email: string,
  password: string,
  handle: string,
): Promise<AuthUser> {
  const token = await getCsrf();
  const res = await signUpWithPassword(
    { email, password, handle },
    csrfInit(token),
  );
  return res.user;
}

/** Exchange a verified Clerk session token for a local kd_session. */
export async function exchangeClerkToken(clerkToken: string): Promise<AuthUser> {
  const token = await getCsrf();
  const res = await exchangeClerkSession({ token: clerkToken }, csrfInit(token));
  return res.user;
}

export async function signOut(): Promise<void> {
  const token = await getCsrf();
  await apiSignOut(csrfInit(token));
}

export async function requestReset(email: string): Promise<void> {
  const token = await getCsrf();
  await requestPasswordReset({ email }, csrfInit(token));
}

export async function completeReset(
  resetToken: string,
  password: string,
): Promise<AuthUser> {
  const token = await getCsrf();
  const res = await completePasswordReset(
    { token: resetToken, password },
    csrfInit(token),
  );
  return res.user;
}
